import { useMutation, useQuery } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { queryClient } from "@via/api/query-client.tsx";
import type { AxiosError } from "axios";
import {
  type ListRemoteStorageCredentialsResponse,
  type RecheckRemoteStorageHostKeyResponse,
  REMOTE_STORAGE_REFUSAL_MESSAGES,
  type RemoteStorageCredentialSummary,
  type RemoteStorageFileListRequestBody,
  type RemoteStorageFileListResponse,
  type RemoteStorageHostKeyRequestBody,
  type RemoteStorageHostKeyResponse,
  type RemoteStorageRefusalReason,
  type UpsertRemoteStorageCredentialRequestBody,
} from "common";

export const REMOTE_STORAGE_CREDENTIALS_KEY = [
  "/api/v1/remote-storage/credentials",
];

export const useRemoteStorageCredentials = () =>
  useQuery({
    queryKey: REMOTE_STORAGE_CREDENTIALS_KEY,
    queryFn: async (): Promise<ListRemoteStorageCredentialsResponse> =>
      (await axios.get("/api/v1/remote-storage/credentials")).data,
  });

export const useSaveRemoteStorageCredential = () =>
  useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id?: number;
      body: UpsertRemoteStorageCredentialRequestBody;
    }): Promise<RemoteStorageCredentialSummary> =>
      (id
        ? await axios.put(`/api/v1/remote-storage/credentials/${id}`, body)
        : await axios.post("/api/v1/remote-storage/credentials", body)
      ).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: REMOTE_STORAGE_CREDENTIALS_KEY,
      }),
  });

export const useDeleteRemoteStorageCredential = () =>
  useMutation({
    mutationFn: async (id: number) =>
      (await axios.delete(`/api/v1/remote-storage/credentials/${id}`)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: REMOTE_STORAGE_CREDENTIALS_KEY,
      }),
  });

export const useDiscoverHostKey = () =>
  useMutation({
    mutationFn: async (
      body: RemoteStorageHostKeyRequestBody
    ): Promise<RemoteStorageHostKeyResponse> =>
      (await axios.post("/api/v1/remote-storage/host-key", body)).data,
  });

export const useRecheckHostKey = () =>
  useMutation({
    mutationFn: async (
      id: number
    ): Promise<RecheckRemoteStorageHostKeyResponse> =>
      (await axios.post(`/api/v1/remote-storage/credentials/${id}/host-key`))
        .data,
  });

export const useRemoteStorageFolderList = () =>
  useMutation({
    mutationFn: async (
      body: RemoteStorageFileListRequestBody
    ): Promise<RemoteStorageFileListResponse> =>
      (await axios.post("/api/data/v1/remote-storage/file-list", body)).data,
  });

export const remoteStorageRefusalReason = (
  error: unknown
): RemoteStorageRefusalReason | null => {
  const reason = (error as AxiosError<{ reason?: string }>)?.response?.data
    ?.reason;
  return reason && reason in REMOTE_STORAGE_REFUSAL_MESSAGES
    ? (reason as RemoteStorageRefusalReason)
    : null;
};

export const remoteStorageErrorMessage = (error: unknown): string => {
  const reason = remoteStorageRefusalReason(error);
  if (reason) return REMOTE_STORAGE_REFUSAL_MESSAGES[reason];
  return (
    (error as AxiosError<{ message?: string }>)?.response?.data?.message ??
    "The remote server could not be reached."
  );
};
